/**
 * Reading the fields of the dimensional-analysis tool: a starting quantity, an optional
 * target, and rows of conversion factors typed as text. Kept apart from the widget so the
 * rules for "which row is wrong and why" are plain functions.
 */

import {
  type FactorSpec,
  parseUnits,
  type UnitEntry,
} from "@/lib/tool-kit/chemistry/dimensional";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

export interface FactorRow {
  id: number;
  lv: string;
  lu: string;
  rv: string;
  ru: string;
  /** An exact (defined) factor does not limit the significant figures. */
  exact: boolean;
}

export type RowProblem = "incompleteRow" | "badRowUnits" | "badFactor";

export type RowRead =
  | { ok: true; spec: FactorSpec }
  | { ok: false; code: RowProblem };

export function readFactorRow(row: FactorRow): RowRead {
  if ([row.lv, row.lu, row.rv, row.ru].some((text) => text.trim() === "")) {
    return { ok: false, code: "incompleteRow" };
  }
  const leftValue = parseMeasuredNumber(row.lv);
  const rightValue = parseMeasuredNumber(row.rv);
  if (leftValue === null || rightValue === null) {
    return { ok: false, code: "badFactor" };
  }
  const leftUnits = parseUnits(row.lu);
  const rightUnits = parseUnits(row.ru);
  if (!(leftUnits.ok && rightUnits.ok)) {
    return { ok: false, code: "badRowUnits" };
  }
  return {
    ok: true,
    spec: {
      leftValue,
      leftUnits: leftUnits.entries,
      rightValue,
      rightUnits: rightUnits.entries,
    },
  };
}

export function isBlankRow(row: FactorRow): boolean {
  return [row.lv, row.lu, row.rv, row.ru].every((text) => text.trim() === "");
}

export type InputProblem =
  | { code: "enterStart" }
  | { code: "badStartValue" | "badStartUnits" | "badTargetUnits" }
  | { code: RowProblem; row: number };

export type Evaluated =
  | { ok: false; problem: InputProblem }
  | {
      ok: true;
      startValue: number;
      startUnits: UnitEntry[];
      target: UnitEntry[] | null;
      specs: FactorSpec[];
      /** Typed numbers that limit the significant figures of the answer. */
      measured: string[];
    };

export function evaluateInputs(input: {
  startText: string;
  startUnitsText: string;
  targetText: string;
  rows: readonly FactorRow[];
}): Evaluated {
  const { startText, startUnitsText, targetText, rows } = input;
  if (startText.trim() === "" || startUnitsText.trim() === "") {
    return { ok: false, problem: { code: "enterStart" } };
  }
  const startValue = parseMeasuredNumber(startText);
  if (startValue === null) {
    return { ok: false, problem: { code: "badStartValue" } };
  }
  const startUnits = parseUnits(startUnitsText);
  if (!startUnits.ok) {
    return { ok: false, problem: { code: "badStartUnits" } };
  }
  const target = targetText.trim() === "" ? null : parseUnits(targetText);
  if (target !== null && !target.ok) {
    return { ok: false, problem: { code: "badTargetUnits" } };
  }
  const specs: FactorSpec[] = [];
  const measured = [startText];
  for (const [index, row] of rows.entries()) {
    if (isBlankRow(row)) {
      continue;
    }
    const read = readFactorRow(row);
    if (!read.ok) {
      return { ok: false, problem: { code: read.code, row: index } };
    }
    specs.push(read.spec);
    if (!row.exact) {
      measured.push(row.lv, row.rv);
    }
  }
  return {
    ok: true,
    startValue,
    startUnits: startUnits.entries,
    target: target?.ok ? target.entries : null,
    specs,
    measured,
  };
}
